import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "@/App";

const HERO_BG =
  "https://images.unsplash.com/photo-1756477558468-b3e485757470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxwaWNrbGViYWxsJTIwY291cnQlMjBhY3Rpb258ZW58MHx8fHwxNzkxNDI5ODY3fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["PORTABLE", "PICKLEBALL COURTS", "ANYWHERE."];

const STATS = [
  { k: "1HR", v: "Quote turnaround" },
  { k: "MELB-WIDE", v: "Metro & regional VIC" },
  { k: "FULL SETUP", v: "We do everything" },
  { k: "ALL AGES", v: "Everyone plays" },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_BG} alt="Pickleball court in action" className="h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/40 to-obsidian" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-0 pt-40 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-6 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt"
        >
          Melbourne's pop-up court crew — events, activations & spaces
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-5xl font-extrabold uppercase leading-[0.92] tracking-tight sm:text-7xl lg:text-8xl"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-volt" : "text-white"}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          className="mt-6 max-w-xl text-base text-white/70 sm:text-lg"
        >
          We deliver, set up and host professional pop-up pickleball courts anywhere in Melbourne & Victoria.
          You bring the people. We bring the party.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-quote"
            onClick={() => scrollToId("#quote")}
            className="group flex items-center gap-2 rounded-full bg-volt px-7 py-4 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-volt-hover"
          >
            Get a 1hr quote
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-packages"
            onClick={() => scrollToId("#why")}
            className="group flex items-center gap-2 rounded-full border border-white/25 px-7 py-4 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-volt hover:text-volt"
          >
            Explore the experience
            <ArrowDown size={18} className="transition-transform group-hover:translate-y-1" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-16 grid grid-cols-2 border-t border-white/10 lg:grid-cols-4"
        >
          {STATS.map((s, i) => (
            <motion.div
              key={s.k}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.05 + i * 0.1, ease: EASE }}
              className="border-r border-white/10 px-4 py-6 last:border-r-0 sm:px-6"
            >
              <p className="font-mono text-sm font-semibold tracking-[0.2em] text-volt">{s.k}</p>
              <p className="mt-1 text-xs text-white/50 sm:text-sm">{s.v}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
